"use client";

import React, { useEffect, useMemo, useState } from "react";
import FilterCard, { FilterCardProps } from "./FilterCard";
import Icon from "../../common/components/Icon";
import Button from "./Button";
import useFilterContext from "../hooks/useFilterContext";
import { Input } from "./Form";
import { useTranslations } from "next-intl";

export interface MultiSelectFilterCardProps
  extends Pick<FilterCardProps, "onSettingsClicked" | "title"> {
  title: string;
  limit?: number;
  searchParamName: string;
  filterItems: Array<{
    label: string;
    value: string;
    showSettings?: boolean;
  }>;
  onSettingsClicked?: FilterCardProps["onSettingsClicked"];
}

const MultiSelectFilterCard: React.FC<MultiSelectFilterCardProps> = ({
  title,
  limit = 4,
  searchParamName,
  filterItems = [],
  onSettingsClicked,
}) => {
  const { formState, setFilterValues } = useFilterContext();
  const t = useTranslations();
  const currentValues = useMemo(() => {
    return formState[searchParamName] || [];
  }, [searchParamName, formState]);

  const [itemLimit, setItemLimit] = useState(
    Math.max(
      filterItems.findLastIndex((i) => {
        return currentValues.includes(i.value);
      }) + 1,
      limit,
    ),
  );

  const validValues = currentValues.filter((f) =>
    filterItems.map((i) => i.value).includes(f),
  );

  useEffect(() => {
    if (validValues?.length && validValues.length !== currentValues.length) {
      console.log(
        "Updating filter values due invalid values detected (after removal of favorite list for example)",
      );
      setFilterValues(searchParamName, validValues);
    }
  }, [validValues, currentValues, setFilterValues, searchParamName]);

  const onChange = (e) => {
    if (e.target.checked) {
      const values = [...currentValues, e.target.value].filter(Boolean);
      setFilterValues(searchParamName, values);
    } else {
      const values = [...currentValues].filter((v) => e.target.value !== v);
      setFilterValues(searchParamName, values);
    }
  };

  const showReset = filterItems.some((i) => currentValues?.includes(i.value));
  const reset = () => {
    setFilterValues(searchParamName, []);
    setItemLimit(limit);
  };

  const showSelectAll = filterItems.some(
    (i) => !currentValues?.includes(i.value),
  );
  const selectAll = () => {
    setFilterValues(
      searchParamName,
      filterItems.map((item) => item.value),
    );
    setItemLimit(filterItems.length);
  };

  const showMore = filterItems.length > itemLimit;
  const loadMore = () => {
    setItemLimit(Infinity);
  };

  if (!filterItems.length) return null;

  return (
    <FilterCard title={title} onSettingsClicked={onSettingsClicked}>
      {filterItems.length > 1 && (
        <div className="filter-select">
          <Button
            className="color-blue fs-8"
            appearance="link"
            onClick={selectAll}
            disabled={!showSelectAll}
            aria-disabled={!showSelectAll}
          >
            {t("select_all")}
          </Button>
          <span className="color-primary-dimmed mx-2"> · </span>
          <Button
            className="fs-8"
            appearance="link"
            onClick={reset}
            disabled={!showReset}
            aria-disabled={!showReset}
          >
            {t("reset")}
          </Button>
        </div>
      )}
      <div className="d-flex flex-column gap-12px gap-lg-2 mt-2">
        {filterItems.slice(0, itemLimit).map((item) => (
          <div
            key={item.value}
            className="d-flex align-items-center justify-content-between"
          >
            <span className="d-flex align-items-start">
              <Input
                type="checkbox"
                id={`${searchParamName}:${item.value}`}
                name={searchParamName}
                value={item.value}
                checked={
                  formState[searchParamName]?.includes(item.value) || false
                }
                onChange={onChange}
              />
              <label
                htmlFor={`${searchParamName}:${item.value}`}
                className="hyphens-auto"
              >
                {item.label}
              </label>
            </span>
            {item.showSettings && onSettingsClicked && (
              <Button
                appearance="quaternary"
                onClick={() => onSettingsClicked(item.value)}
                icon={<Icon icon="edit" className="icon--checkbox" />}
              ></Button>
            )}
          </div>
        ))}
      </div>

      {showMore && (
        <button className="color-blue fs-8 mt-2" onClick={loadMore}>
          {t("show_more")}
        </button>
      )}
    </FilterCard>
  );
};

export default MultiSelectFilterCard;
